import type { PushSubscriptionJSON } from './types';
import { API_BASE_URL } from '../config';

const PUBLIC_KEY_ENDPOINT = '/api/public-key';
const SUBSCRIPTIONS_ENDPOINT = '/api/subscriptions';
const PREFERENCES_ENDPOINT = '/api/preferences';
const SUBSCRIPTION_STATUS_ENDPOINT = '/api/subscriptions/status';

type SubscriptionOptions = {
  userId?: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
};

export type SubscriptionPayload = {
  subscription: PushSubscriptionJSON;
} & SubscriptionOptions;

export type PublicKeyResponse = {
  publicKey: string;
};

export type UserPreferencesPayload = {
  userId: string;
  jobTitle: string;
  jobLocation?: string;
  alertsOptIn: boolean;
};

export type UserPreferencesResponse = {
  jobTitle?: string;
  jobLocation?: string;
  alertsOptIn: boolean;
};

export type SubscriptionStatusResponse = {
  hasSubscription: boolean;
  count: number;
};

export async function fetchPublicKey(): Promise<string> {
  const response = await fetch(`${API_BASE_URL}${PUBLIC_KEY_ENDPOINT}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch public key (${response.status})`);
  }
  const data = (await response.json()) as PublicKeyResponse;
  if (!data?.publicKey) {
    throw new Error('No publicKey returned from backend.');
  }
  return data.publicKey;
}

export async function saveSubscription(
  payload: SubscriptionPayload
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}${SUBSCRIPTIONS_ENDPOINT}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to store subscription (${response.status}): ${text}`);
  }
}

export async function saveUserPreferences(payload: UserPreferencesPayload): Promise<void> {
  const response = await fetch(`${API_BASE_URL}${PREFERENCES_ENDPOINT}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to store preferences (${response.status}): ${text}`);
  }
}

export async function deleteSubscription(endpoint: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}${SUBSCRIPTIONS_ENDPOINT}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ endpoint }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to remove subscription (${response.status}): ${text}`);
  }
}

export async function fetchUserPreferences(userId: string): Promise<UserPreferencesResponse> {
  const response = await fetch(`${API_BASE_URL}${PREFERENCES_ENDPOINT}?userId=${encodeURIComponent(userId)}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch preferences (${response.status})`);
  }

  const data = (await response.json()) as UserPreferencesResponse | undefined;
  return {
    jobTitle: data?.jobTitle,
    jobLocation: data?.jobLocation,
    alertsOptIn: data?.alertsOptIn ?? false,
  };
}

export async function fetchSubscriptionStatus(userId: string): Promise<SubscriptionStatusResponse> {
  const response = await fetch(
    `${API_BASE_URL}${SUBSCRIPTION_STATUS_ENDPOINT}?userId=${encodeURIComponent(userId)}`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch subscription status (${response.status})`);
  }

  const data = (await response.json()) as SubscriptionStatusResponse | undefined;
  return {
    hasSubscription: data?.hasSubscription ?? false,
    count: data?.count ?? 0,
  };
}

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}
